import React from 'react';
import { motion } from 'framer-motion';

/**
 * Oblique Design System - Button Component
 * 
 * A fully-featured button using CSS custom properties from the design system.
 * Supports variants, sizes, icons, loading state, and full-width mode.
 * 
 * Styling: Tailwind v4 utilities + CSS custom properties (no inline style objects).
 */
export function Button({ 
    children, 
    variant = 'primary',    // 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'
    size = 'md',            // 'sm' | 'md' | 'lg' | 'icon'
    className = '',
    fullWidth = false,
    onClick,
    disabled = false,
    loading = false,
    iconLeft,               // React element
    iconRight,              // React element
    type = 'button',
    ...props 
}) {
    const variants = {
        primary:   'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border/50',
        ghost:     'hover:bg-accent hover:text-accent-foreground text-foreground',
        danger:    'bg-destructive/10 text-destructive hover:bg-destructive/20 border border-destructive/20',
        outline:   'border border-input bg-background hover:bg-accent hover:text-accent-foreground text-foreground shadow-sm'
    };

    const sizes = {
        sm:   'h-8 px-3 text-xs rounded-md gap-1.5',
        md:   'h-9 px-4 py-2 text-sm rounded-md gap-2',
        lg:   'h-10 px-6 text-sm rounded-lg gap-2',
        icon: 'h-9 w-9 flex items-center justify-center rounded-md'
    };

    const baseStyles = [
        'inline-flex items-center justify-center',
        'font-medium whitespace-nowrap cursor-pointer',
        'transition-colors duration-150 ease-in-out',
        'outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        'select-none'
    ].join(' ');
    
    const combinedClasses = [
        baseStyles,
        variants[variant] || variants.primary,
        sizes[size] || sizes.md,
        fullWidth ? 'w-full' : '',
        (loading || disabled) ? 'pointer-events-none' : '',
        loading ? 'opacity-80' : '',
        className
    ].filter(Boolean).join(' ');

    return (
        <motion.button
            whileTap={{ scale: disabled || loading ? 1 : 0.97 }}
            whileHover={{ scale: disabled || loading ? 1 : 1.01 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            type={type}
            className={combinedClasses}
            onClick={onClick}
            disabled={disabled || loading}
            aria-disabled={disabled || loading}
            aria-busy={loading}
            {...props}
        >
            {/* Loading spinner */}
            {loading && (
                <svg
                    className="animate-spin shrink-0"
                    width={size === 'sm' ? 14 : 16}
                    height={size === 'sm' ? 14 : 16}
                    viewBox="0 0 24 24"
                    fill="none"
                >
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
            )}

            {/* Left icon */}
            {!loading && iconLeft && (
                <span className="flex items-center shrink-0">
                    {iconLeft}
                </span>
            )}

            {/* Label — hidden for icon-only */}
            {size !== 'icon' && children && (
                <span>{children}</span>
            )}

            {/* Icon-only children */}
            {size === 'icon' && children}

            {/* Right icon */}
            {iconRight && (
                <span className="flex items-center shrink-0">
                    {iconRight}
                </span>
            )}
        </motion.button>
    );
}
